import {memo, forwardRef, useMemo} from 'react';

import {useAppDispatch, useAppSelector} from 'stores/redux_store';

import {getTeammate, makeGetChannel} from 'mattermost-redux/selectors/entities/channels';
import {getPost} from 'mattermost-redux/selectors/entities/posts';
import {type Post} from 'mattermost-redux/types/posts';

import GenericCreateComment from 'components/create_comment';
import Constants from 'utils/constants';
import {Permissions, Posts} from 'mattermost-redux/constants';
import {type GlobalState} from 'types/store';
import {getReadOnlyStateByChannelId} from 'features/channels';
import {joinChannelById} from 'actions/views/channel';
import * as Utils from 'utils/utils';
import {AdvancedEditorCreateComment} from 'features/editor/components/create_comment';
import {
    ArchivedChannel,
    RestrictedChannel,
    DeactivatedChannel,
    CanPostOnlyInChannel,
    ReadOnlyChannelWithJoinLink,
} from 'components/editor_state';
import {haveIChannelPermission} from 'mattermost-redux/selectors/entities/roles';
import {getLatestPostId} from 'utils/post_utils';
import {getPostIdsForRootPost} from 'features/threads/selectors/get_posts_for_root_post';

import {useVirtualizedContext} from './virtualized_context';

type Props = {
    focusOnMount: boolean;
    threadId: string;
    isThreadView?: boolean;
    rootId: Post['id'];
};

const CreateComment = forwardRef<HTMLDivElement, Props>((props: Props, ref) => {
    const dispatch = useAppDispatch();
    const {scrollToBottom} = useVirtualizedContext();
    const getChannel = useMemo(makeGetChannel, []);
    const rootPost = useAppSelector((state: GlobalState) => getPost(state, props.threadId)!);
    const channel = useAppSelector((state: GlobalState) => getChannel(state, {id: rootPost.channel_id}));
    const teammate = useAppSelector((state) => getTeammate(state, channel));
    const isChannelReadOnly = useAppSelector((state: GlobalState) => getReadOnlyStateByChannelId(state, channel.id));
    const rootDeleted = rootPost.state === Posts.POST_DELETED;
    const isFakeDeletedPost = rootPost.type === Constants.PostTypes.FAKE_PARENT_DELETED;
    const newEditorEnabled = useAppSelector((state: GlobalState) =>
        Utils.isFeatureEnabled(Constants.PRE_RELEASE_FEATURES.NEW_EDITOR, state),
    );
    const postIds = useAppSelector((state: GlobalState) => getPostIdsForRootPost(state, props.rootId));
    const latestPostId = useMemo(() => getLatestPostId(postIds), [postIds]);
    const channelType = channel.type;
    const channelIsArchived = channel.delete_at !== 0;

    const canIPost = useAppSelector((state) => haveIChannelPermission(state, channel.team_id, channel.id, Permissions.CREATE_POST));
    const canIPostInThread = useAppSelector((state) => haveIChannelPermission(state, channel.team_id, channel.id, Permissions.CREATE_POST_IN_THREAD));

    const handleJoinChannelBtnClick = () => {
        dispatch(joinChannelById(channel.id));
    };

    if (channelType === Constants.DM_CHANNEL && teammate?.delete_at) {
        return <DeactivatedChannel />;
    }

    if (isFakeDeletedPost) {
        return null;
    }

    if (channelIsArchived) {
        return <ArchivedChannel />;
    }

    if (isChannelReadOnly) {
        return <ReadOnlyChannelWithJoinLink isThread={true} onClick={handleJoinChannelBtnClick}/>;
    }

    if (!canIPost && !canIPostInThread) {
        return <RestrictedChannel channelType={channelType} />;
    }

    if (canIPost && !canIPostInThread) {
        return <CanPostOnlyInChannel />;
    }

    if (newEditorEnabled) {
        return <AdvancedEditorCreateComment teammate={teammate} latestPostId={latestPostId} channelId={channel.id} {...props} />;
    }

    return (
        <div
            className='post-create__container'
            ref={ref}
        >
            <GenericCreateComment
                focusOnMount={props.focusOnMount}
                channelId={channel.id}
                latestPostId={latestPostId}
                rootDeleted={rootDeleted}
                rootId={props.threadId}
                isThreadView={props.isThreadView}
                scrollToBottom={scrollToBottom}
            />
        </div>
    );
});

export default memo(CreateComment);
